/**
 * useFixedTimestep — a deterministic fixed-timestep game loop shared by the
 * continuous arcade mini-games.
 *
 * Continuous games (platformers, runners, lane shooters) must advance their
 * pure model in fixed increments so the simulation is frame-rate independent
 * and reproducible. This hook implements the canonical browser game-loop
 * pattern once, so each game does not re-roll its own accumulator:
 *   - schedule the next animation frame first, so a slow frame still re-queues;
 *   - accumulate real elapsed time from the rAF timestamp;
 *   - cap the per-frame delta to avoid the "spiral of death";
 *   - drain the accumulator in whole fixed steps, calling `onStep(dt)` for each;
 *   - freeze the baseline when the tab is hidden so time does not jump on return;
 *   - always cancel the loop on unmount.
 *
 * The loop is presentation only — it drives how often the pure model advances,
 * never what the model decides. The structural verdict stays in the model.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useEffect, useRef } from "react";

/** Largest delta (seconds) applied in one frame — spiral-of-death guard. */
export const MAX_FRAME_DELTA = 0.25;

export interface FixedTimestepOptions {
  /** Whether the loop is currently running. When false, the loop is idle. */
  readonly running: boolean;
  /** Seconds per fixed simulation step (e.g. 1 / 60). */
  readonly stepSeconds: number;
  /** Advance the simulation by exactly one fixed step. */
  readonly onStep: (stepSeconds: number) => void;
  /**
   * Optional per-frame callback after the accumulator drains, given the
   * leftover fraction in [0, 1) for render interpolation.
   */
  readonly onFrame?: (alpha: number) => void;
}

/**
 * Drive `onStep` at a fixed cadence while `running` is true. The callbacks are
 * read through refs, so passing fresh closures each render does not restart the
 * loop; only the `running` flag and `stepSeconds` gate it.
 */
export function useFixedTimestep({
  running,
  stepSeconds,
  onStep,
  onFrame,
}: FixedTimestepOptions): void {
  const onStepRef = useRef(onStep);
  const onFrameRef = useRef(onFrame);
  const stepRef = useRef(stepSeconds);
  const accumulatorRef = useRef(0);
  const lastTsRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  onStepRef.current = onStep;
  onFrameRef.current = onFrame;
  stepRef.current = stepSeconds > 0 ? stepSeconds : 1 / 60;

  useEffect(() => {
    if (!running) {
      lastTsRef.current = null;
      accumulatorRef.current = 0;
      return undefined;
    }

    const loop = (ts: number): void => {
      rafRef.current = requestAnimationFrame(loop);
      const last = lastTsRef.current;
      lastTsRef.current = ts;
      if (last === null) return;
      const frameDt = Math.min(MAX_FRAME_DELTA, (ts - last) / 1000);
      accumulatorRef.current += frameDt;
      const step = stepRef.current;
      while (accumulatorRef.current >= step) {
        onStepRef.current(step);
        accumulatorRef.current -= step;
      }
      onFrameRef.current?.(accumulatorRef.current / step);
    };

    rafRef.current = requestAnimationFrame(loop);
    return (): void => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTsRef.current = null;
    };
  }, [running]);

  // Freeze the baseline when the tab is hidden so accumulated time does not
  // surge on return; the next visible frame re-establishes lastTs.
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.hidden) {
        lastTsRef.current = null;
        accumulatorRef.current = 0;
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return (): void => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
